import { useState } from 'react'
import { Trash2, Edit, Shield, Mail, UserCircle, RefreshCw, CheckCircle, XCircle } from 'lucide-react'
import { Usuario } from '../tipos/usuario'
import { useDeleteUsuario } from '../hooks/useUsuario'
import { usePagination } from '../../../hooks/usePagination'
import { PaginationControls } from '../../../componentesCompartilhados/paginacao/PaginationControls'

interface UsuarioListProps {
  usuarios: Usuario[]
  isLoading: boolean
  onRefresh: () => void
  onEditUsuario: (usuario: Usuario) => void
}

export function UsuarioList({ usuarios, isLoading, onRefresh, onEditUsuario }: UsuarioListProps) {
  const deleteMutation = useDeleteUsuario()
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<Usuario | null>(null)

  const {
    paginatedItems: usuariosPaginados,
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    nextPage,
    previousPage,
  } = usePagination(usuarios, {
    totalItems: usuarios.length,
    itemsPerPage: 5,
  })

  const handleDelete = (usuario: Usuario) => {
    setUsuarioSelecionado(null)
    if (!usuario.id) return
    deleteMutation.mutate(usuario.id)
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando usuários...</p>
          <p className="text-sm text-gray-500">Aguarde enquanto buscamos os dados</p>
        </div>
      </div>
    )
  }

  if (!usuarios.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum usuário encontrado</h3>
          <p className="text-gray-600 mb-1">Cadastre um usuário para começar a gerenciar permissões.</p>
          <p className="text-sm text-gray-500">Utilize o botão "Novo Usuário" para iniciar.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-success-500 to-brand-500 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <UserCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Usuários do Sistema</h3>
            <p className="text-sm text-gray-600">{usuarios.length} usuário(s) encontrado(s)</p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuário
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pessoa vinculada
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Papéis
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {usuariosPaginados.map((usuario) => (
              <tr key={usuario.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center font-semibold">
                      {usuario.nome.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{usuario.nome}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                        {usuario.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{usuario.pessoaNome || 'Não vinculado'}</div>
                    <div className="text-xs text-gray-500">ID Pessoa: {usuario.pessoaId || '-'}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-2">
                    {usuario.papeisNomes?.length ? (
                      usuario.papeisNomes.map((papel) => (
                        <span key={papel} className="px-2 py-1 bg-brand-50 text-brand-700 text-xs font-medium rounded-full">
                          {papel}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">Sem papéis</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {usuario.ativo ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-success-700 bg-success-100 rounded-full">
                      <CheckCircle className="w-4 h-4" />
                      Ativo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                      <XCircle className="w-4 h-4" />
                      Inativo
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEditUsuario(usuario)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setUsuarioSelecionado(usuario)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={usuarios.length}
        startIndex={startIndex}
        endIndex={endIndex}
        onPreviousPage={previousPage}
        onNextPage={nextPage}
        itemLabel="usuário"
      />

      {/* Modal simples de confirmação */}
      {usuarioSelecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Excluir usuário</h3>
            <p className="text-sm text-gray-600 mb-4">
              Tem certeza que deseja excluir o usuário <strong>{usuarioSelecionado.nome}</strong>? Essa ação não
              poderá ser desfeita.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setUsuarioSelecionado(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(usuarioSelecionado)}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                disabled={deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

